import { useState } from "react";
import { useMutation } from "@apollo/client";
import { intl } from "LocaleWrapper";
import { Button } from "@cofano/cck";
import { Tooltip } from "@mui/material";
import { useConfirm } from "components/hooks/useConfirm";
import { usePermission } from "imports/Utils";
import { DeleteDeclarationDocument } from "__generated__/view/declarations/graphql/declaration.mutations.generated";

import { CustomDialog } from "components/dialogs/Dialogs";
import { ContractLink } from "utils/LinkUtils";

import DeclarationLines, { DeclarationLinesState } from "../../DeclarationLines";

type DeclarationDialogProps = {
	open: boolean;
	onClose: () => void;
	charterId: number;
	declarationId?: number;
	invoiceGroupType?: { id?: number | null; name?: string | null } | null;
	onSaved?: () => void;
};

const DeclarationDialog = ({ open, onClose, charterId, declarationId, invoiceGroupType, onSaved }: DeclarationDialogProps) => {
	const [ formState, setFormState ] = useState<DeclarationLinesState>({
		saving: false,
		disabled: false,
		isDeclarationClosed: false,
		isDeclarable: true,
		hasAgreements: false,
	});

	const { isAdmin } = usePermission();
	const confirm = useConfirm();
	const [ deleteDeclaration, { loading: deleteLoading } ] = useMutation(DeleteDeclarationDocument);

	const currentDeclaration = formState.declaration;

	const saveTooltip = formState.isDeclarationClosed
		? <span>{intl.formatMessage({ id: "DECLARATION_IS_CLOSED" })}</span>
		: !formState.isDeclarable
			? <span>
				{intl.formatMessage({ id: "CAN_NOT_DECLARE_CONTRACT_NOT_FINAL" })}
				{formState.contract?.id && <>: <ContractLink contract={formState.contract} /></>}
			</span>
			: "";

	return (
		<CustomDialog
			open={open}
			onClose={onClose}
			title={invoiceGroupType?.name
				? `${invoiceGroupType.name} ${intl.formatMessage({ id: "DECLARATION" }).toLowerCase()}`
				: intl.formatMessage({ id: "DECLARATION" })}
			maxWidth="sm"
			actions={<>
				{isAdmin && currentDeclaration && (
					<Button
						color="danger"
						variant="outlined"
						size="small"
						loading={deleteLoading}
						onClick={() => {
							confirm({
								type: "WARNING",
								title: intl.formatMessage({ id: "DECLARATION_DELETE_CONFIRM" }),
							}).then(() => {
								deleteDeclaration({ variables: { id: currentDeclaration.id } }).then(() => {
									onSaved?.();
									onClose();
								});
							});
						}}>
						{intl.formatMessage({ id: "DELETE_DECLARATION" })}
					</Button>
				)}
				{formState.hasAgreements && (
					<Tooltip placement="top" title={saveTooltip}>
						<span>
							<Button
								type="submit"
								form="declaration-creation"
								loading={formState.saving}
								disabled={formState.disabled || formState.saving}
								size="small">
								{intl.formatMessage({ id: "DECLARE" })}
							</Button>
						</span>
					</Tooltip>
				)}
			</>}>

			<DeclarationLines
				charterId={charterId}
				declarationId={declarationId}
				hideSaveButton
				invoiceGroupType={invoiceGroupType}
				onStateChange={setFormState}
				refetch={() => {
					onSaved?.();
					onClose();
				}}
			/>
		</CustomDialog>
	);
};

export default DeclarationDialog;
